import type { MonetisationModelOutput } from "@/features/tools/proToolTypes";

type MonetisationModelResultProps = {
  output: MonetisationModelOutput;
};

function formatModelKey(value: string): string {
  return value.replace(/_/g, " ");
}

function ListBlock({ items }: { items: string[] }) {
  if (items.length === 0) {
    return (
      <p className="rounded-md bg-slate-50 p-3 text-sm text-slate-500">
        No items were returned for this section.
      </p>
    );
  }

  return (
    <ul className="grid gap-2 text-sm leading-6 text-slate-700">
      {items.map((item) => (
        <li key={item} className="min-w-0 break-words rounded-md bg-slate-50 p-3">
          {item}
        </li>
      ))}
    </ul>
  );
}

export function MonetisationModelResult({
  output,
}: MonetisationModelResultProps) {
  return (
    <section className="min-w-0 rounded-lg border border-emerald-200 bg-white p-4 shadow-sm sm:p-6">
      <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-emerald-700">
            Monetisation recommendation
          </p>
          <h3 className="mt-2 break-words text-2xl font-semibold tracking-normal text-slate-950">
            {output.recommendedModel.label}
          </h3>
          <p className="mt-1 text-sm font-semibold capitalize text-slate-600">
            {formatModelKey(output.recommendedModel.modelKey)}
          </p>
        </div>
        <div className="rounded-md bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">
          Commercial path suggested
        </div>
      </div>

      <p className="mt-5 whitespace-pre-wrap break-words text-sm leading-7 text-slate-700">
        {output.summary}
      </p>

      <div className="mt-6 grid min-w-0 gap-5 lg:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <h4 className="text-sm font-semibold text-slate-950">
            Why this model fits
          </h4>
          <p className="mt-2 text-sm leading-6 text-slate-700">
            {output.recommendedModel.whyItFits}
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <h4 className="text-sm font-semibold text-slate-950">
            Positioning angle
          </h4>
          <p className="mt-2 text-sm leading-6 text-slate-700">
            {output.positioningAngle}
          </p>
        </div>
      </div>

      <div className="mt-6 grid min-w-0 gap-5 lg:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <h4 className="text-sm font-semibold text-slate-950">
            Pricing guidance
          </h4>
          <dl className="mt-3 grid gap-3 text-sm leading-6 text-slate-700">
            <div>
              <dt className="font-semibold text-slate-950">Price type</dt>
              <dd>{output.pricingGuidance.suggestedPriceType}</dd>
            </div>
            <div>
              <dt className="font-semibold text-slate-950">Suggested range</dt>
              <dd>{output.pricingGuidance.suggestedRange}</dd>
            </div>
            <div>
              <dt className="font-semibold text-slate-950">Rationale</dt>
              <dd>{output.pricingGuidance.rationale}</dd>
            </div>
          </dl>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <h4 className="text-sm font-semibold text-slate-950">
            Audience fit
          </h4>
          <dl className="mt-3 grid gap-3 text-sm leading-6 text-slate-700">
            <div>
              <dt className="font-semibold text-slate-950">Primary audience</dt>
              <dd>{output.audienceFit.primaryAudience}</dd>
            </div>
            <div>
              <dt className="font-semibold text-slate-950">Why they want it</dt>
              <dd>{output.audienceFit.whyTheyWouldWantIt}</dd>
            </div>
            <div>
              <dt className="font-semibold text-slate-950">Buying trigger</dt>
              <dd>{output.audienceFit.buyingTrigger}</dd>
            </div>
          </dl>
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-slate-200 bg-white p-4">
        <h4 className="text-sm font-semibold text-slate-950">
          Offer structure
        </h4>
        <div className="mt-3 grid min-w-0 gap-5 lg:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Core promise
            </p>
            <p className="mt-1 text-sm leading-6 text-slate-700">
              {output.offerStructure.corePromise}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Delivery format
            </p>
            <p className="mt-1 text-sm leading-6 text-slate-700">
              {output.offerStructure.deliveryFormat}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Call to action
            </p>
            <p className="mt-1 text-sm leading-6 text-slate-700">
              {output.offerStructure.callToAction}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Included assets
            </p>
            <div className="mt-2">
              <ListBlock items={output.offerStructure.includedAssets} />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 grid min-w-0 gap-5 lg:grid-cols-2">
        <div>
          <h4 className="text-sm font-semibold text-slate-950">
            Alternative models
          </h4>
          <div className="mt-3 grid gap-3">
            {output.alternativeModels.map((model) => (
              <div
                key={`${model.modelKey}-${model.label}`}
                className="min-w-0 rounded-md border border-slate-200 bg-white p-4"
              >
                <p className="break-words text-sm font-semibold text-slate-950">
                  {model.label}
                </p>
                <p className="mt-1 break-words text-sm leading-6 text-slate-600">
                  {model.whenToUseInstead}
                </p>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-slate-950">
            What to avoid
          </h4>
          <div className="mt-3">
            <ListBlock items={output.whatToAvoid} />
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-4">
        <h4 className="text-sm font-semibold text-slate-950">
          Recommended next step
        </h4>
        <p className="mt-2 text-sm leading-6 text-slate-700">
          {output.recommendedNextStep}
        </p>
      </div>
    </section>
  );
}
